"use client";

import { Component, type ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { reportClientError } from "@/lib/error-log";

interface Props {
  children: ReactNode;
  /** Kurzer, undramatischer Bereichsname fuer den Fallback-Text (z.B. "Kennzahlen-Bereich")
   * - bewusst NICHT die vollflaechige error.tsx-Behandlung, ein einzelner fehlgeschlagener
   * Abschnitt soll nicht wie ein kompletter Seitenausfall wirken. */
  sectionLabel: string;
}

interface State {
  hasError: boolean;
}

/**
 * Faengt einen Fehler aus GENAU EINEM Seitenabschnitt ab (typischerweise ein Suspense-Block
 * um eine datenintensive Server Component), statt ihn bis zur naechsten routeweiten
 * error.tsx durchschlagen zu lassen - Erik-Feedback 2026-07-22: ein einzelner
 * fehlschlagender Datenpunkt darf nie mehr die GANZE Aktienseite (inkl. bereits
 * erfolgreich geladenem Profil/Score/Kurschart) mit "Daten gerade nicht abrufbar"
 * abstuerzen lassen. Muss eine Klassenkomponente sein - React hat aktuell keine
 * Hook-Entsprechung fuer Error Boundaries. Faengt auch Fehler aus Server-Component-Kindern
 * ab, die per Suspense gestreamt werden - der Fehler erreicht den Client ueber denselben
 * React-Reconciliation-Pfad wie jeder andere Render-Fehler.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo: { componentStack?: string | null }) {
    console.error(`[SectionErrorBoundary] ${this.props.sectionLabel}:`, error, errorInfo.componentStack);
    // Next.js redigiert die echte Fehlermeldung von Server-Component-Fehlern in
    // Produktion ("An error occurred in the Server Components render...") und haengt
    // stattdessen eine `digest`-Kennung an - ohne die ist ein solcher Fehler aus
    // error_logs allein nicht mehr auf eine konkrete Codestelle zurueckfuehrbar
    // (2026-08-10 bei "Mein Depot" beobachtet). Kein eigenes DB-Feld dafuer noetig,
    // reicht direkt in die message-Spalte eingehaengt.
    const digest = (error as { digest?: string } | null)?.digest;
    const baseMessage = error instanceof Error ? error.message : String(error);
    // Root-Cause-Fix Teil 11 (2026-08-11, "Mein Depot"): bei React-Fehler #130
    // (Element type undefined) reichte selbst ein auf 50 Frames erweitertes
    // error.stack (Teil 10) nie bis zur App-Aufrufstelle zurueck - vermutlich, weil
    // der Fehler innerhalb eines von React selbst geplanten Arbeitspakets entsteht,
    // nicht als direkte synchrone Folge eines App-Funktionsaufrufs. React's EIGENER,
    // vom JS-Call-Stack unabhaengiger `componentStack` (zweiter componentDidCatch-
    // Parameter, siehe react.dev) zeigt stattdessen die Komponenten-VERSCHACHTELUNG
    // zum Zeitpunkt des Fehlers - genau das fehlte bisher komplett (nur `error`,
    // nie `errorInfo` entgegengenommen). In den `stack`-Wert eingehaengt statt in
    // `message`, um dessen bisherige Bedeutung (JS-Stack) nicht zu vermischen.
    const stack = error instanceof Error ? error.stack : undefined;
    reportClientError({
      source: `SectionErrorBoundary:${this.props.sectionLabel}`,
      message: digest ? `${baseMessage} [digest: ${digest}]` : baseMessage,
      stack: errorInfo.componentStack
        ? `${stack ?? ""}\n\n--- componentStack ---${errorInfo.componentStack}`
        : stack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-hairline bg-muted/30 p-6 text-center">
          <AlertCircle className="mx-auto h-5 w-5 text-muted-foreground" />
          <p className="mt-2 text-sm font-medium text-ink">{this.props.sectionLabel} gerade nicht verfügbar</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Der Rest der Seite funktioniert weiterhin - bitte versuche es in Kürze erneut.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
